import {useCallback, useContext} from 'react'

import type {UpdateColumnValueAction} from '../api/columns/contracts/domain'
import {useNullableBoardContext} from '../components/board/board-context'
import type {ApplyTransientFilterOpts} from '../components/filter-bar/search-context'
import {TableInstanceContext} from '../components/react_table/table-provider'
import {ViewType} from '../helpers/view-type'
import type {MemexItemModel} from '../models/memex-item-model'
import {useViewType} from './use-view-type'

const defaultUpdateActions: Array<UpdateColumnValueAction> = []

export function useGetLastItem({
  updateActions = defaultUpdateActions,
  matchesSearchQuery,
}: {
  updateActions: ReadonlyArray<UpdateColumnValueAction>
  matchesSearchQuery: (item: MemexItemModel, applyTransientFilter?: ApplyTransientFilterOpts) => boolean
}): () => MemexItemModel | undefined {
  const {viewType} = useViewType()
  const tableInstance = useContext(TableInstanceContext)
  const boardContext = useNullableBoardContext()

  return useCallback(
    function getLastItem() {
      if (viewType === ViewType.Table && tableInstance) {
        // We are in the table view, so use the table data to find the previous item
        const {flatRows, groupedRows} = tableInstance
        const [updateAction] = updateActions

        const rows = updateAction
          ? groupedRows?.find(group => {
              if (!updateActions[0]?.value) return false

              if (
                typeof updateAction.value === 'object' &&
                'id' in updateAction.value &&
                'id' in group.groupedSourceObject.value
              ) {
                return updateAction.value.id === group.groupedSourceObject.value.id
              }
              return updateAction.value === group.groupedValue
            })?.subRows
          : flatRows.filter(row => !row.isGrouped)

        return rows?.map(row => row.original).findLast(item => matchesSearchQuery(item))
      }

      if (viewType === ViewType.Board && updateActions.length > 0) {
        const [verticalColumnUpdateAction, horizontalColumnUpdateAction] = updateActions
        const {groupedItems} = boardContext || {}

        let verticalGroupId: string | number | undefined = undefined
        if (
          verticalColumnUpdateAction &&
          typeof verticalColumnUpdateAction?.value === 'object' &&
          'id' in verticalColumnUpdateAction.value
        ) {
          verticalGroupId = verticalColumnUpdateAction?.value?.id
        }

        if (!verticalGroupId) return

        // If there is no horizontal column update action, we filter the vertical column and find the last item in that column
        if (!horizontalColumnUpdateAction || !groupedItems?.isHorizontalGrouped) {
          return groupedItems?.allItemsByVerticalGroup?.[verticalGroupId]?.findLast(item => matchesSearchQuery(item))
        }

        if (typeof horizontalColumnUpdateAction?.value === 'object' && 'id' in horizontalColumnUpdateAction.value) {
          const horizontalGroupId = horizontalColumnUpdateAction.value.id

          return groupedItems.horizontalGroups
            ?.find(group => group.value === horizontalGroupId)
            ?.itemsByVerticalGroup[verticalGroupId]?.findLast(item => matchesSearchQuery(item))
        }
      }
    },
    [boardContext, matchesSearchQuery, tableInstance, updateActions, viewType],
  )
}
